import { useState, type FormEvent } from 'react';
import { useAuth } from '../state/AuthContext';

// Sign-in / create-account screen. Email + password against /api/auth/login or
// /api/auth/register, which set an httpOnly session cookie good for 30 days. No
// shared key, no magic link, no "paste this URL" - you type your credentials like
// any other site, and you stay signed in until you sign out.
//
// Create-account is only accepted for addresses named in ADMIN_EMAILS /
// APPROVED_EMAILS; anything else is refused by the server. The register endpoint
// existed before this screen did, which meant a stranger could reach it with curl
// while the owner had no button for it.
export function SignInPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'create'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [registerToken, setRegisterToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const creating = mode === 'create';

  // On success, do a REAL navigation rather than flipping React state. The role
  // flip would unmount this form in the same commit the fetch resolves, and the
  // password manager's save prompt is anchored to that form - it needs the form
  // to outlive the submit. A full load also re-reads identity + data from the
  // server, which is the safer default for this app. We do not clear `busy` on
  // success: the button stays disabled through the navigation instead of flashing
  // back to its idle label.
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (creating) await signUp(name, email, password, registerToken);
      else await signIn(email, password);
      window.location.assign('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : creating ? 'Create account failed' : 'Sign in failed');
      setBusy(false);
    }
  }

  const field: React.CSSProperties = {
    width: '100%', padding: '10px 12px', marginTop: 6,
    background: 'rgba(8,14,24,0.72)', color: '#dce8f7',
    border: '1px solid rgba(170,195,230,0.18)', borderRadius: 6,
    font: '14px/1.4 ui-monospace, monospace',
  };
  const label: React.CSSProperties = {
    display: 'block', marginTop: 16, fontSize: 12, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: '#8fb3d4',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <form onSubmit={onSubmit} style={{
        width: '100%', maxWidth: 380, padding: 32,
        background: 'rgba(18,26,40,0.72)', backdropFilter: 'blur(16px)',
        border: '1px solid rgba(170,195,230,0.18)', borderRadius: 12,
      }}>
        <div style={{
          fontSize: 26, fontWeight: 700, letterSpacing: '0.18em',
          color: '#b8cee6', marginBottom: 4,
        }}>WORK</div>
        <div style={{ fontSize: 13, color: '#8fb3d4', marginBottom: 8 }}>
          {creating ? 'Create your account' : 'Sign in to continue'}
        </div>

        {creating && (
          <>
            <label style={label} htmlFor="name">Name</label>
            <input id="name" style={field} type="text" autoComplete="name"
              value={name} onChange={e => setName(e.target.value)} required autoFocus />
          </>
        )}

        <label style={label} htmlFor="email">Email</label>
        <input id="email" style={field} type="email" autoComplete="username"
          value={email} onChange={e => setEmail(e.target.value)} required autoFocus={!creating} />

        <label style={label} htmlFor="password">Password</label>
        <input id="password" style={field} type="password"
          autoComplete={creating ? 'new-password' : 'current-password'}
          minLength={creating ? 8 : undefined}
          value={password} onChange={e => setPassword(e.target.value)} required />
        {creating && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#7f9dbb' }}>
            At least 8 characters. Your password manager can generate one.
          </div>
        )}

        {creating && (
          <>
            <label style={label} htmlFor="registerToken">Registration code</label>
            <input id="registerToken" style={field} type="password" autoComplete="off"
              value={registerToken} onChange={e => setRegisterToken(e.target.value)} required />
            <div style={{ marginTop: 6, fontSize: 12, color: '#7f9dbb' }}>
              The one-time code for this site. Ask Keith if you don't have it.
            </div>
          </>
        )}

        {error && (
          <div role="alert" style={{
            marginTop: 16, padding: '10px 12px', borderRadius: 6,
            border: '1px solid rgba(240,140,140,0.55)', background: 'rgba(60,18,18,0.72)',
            color: '#ffd9d9', font: '13px/1.45 ui-monospace, monospace',
          }}>{error}</div>
        )}

        <button type="submit" disabled={busy} style={{
          width: '100%', marginTop: 24, padding: '11px 16px', borderRadius: 6,
          border: '1px solid rgba(184,206,230,0.5)',
          background: busy ? 'rgba(60,80,110,0.5)' : 'rgba(88,124,170,0.65)',
          color: '#eaf2fb', font: '14px/1 ui-monospace, monospace',
          cursor: busy ? 'default' : 'pointer',
        }}>{busy
          ? (creating ? 'Creating account...' : 'Signing in...')
          : (creating ? 'Create account' : 'Sign in')}</button>

        <button type="button" onClick={() => { setMode(creating ? 'signin' : 'create'); setError(null); }}
          style={{
            width: '100%', marginTop: 12, padding: '8px 16px', borderRadius: 6,
            border: '1px solid transparent', background: 'transparent',
            color: '#8fb3d4', font: '13px/1 ui-monospace, monospace', cursor: 'pointer',
          }}>
          {creating ? 'Have an account? Sign in' : 'Create an account'}
        </button>
      </form>
    </div>
  );
}
